import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useDispatch } from 'react-redux';

import {
  customErrorShackBar,
  customMessageShackBar,
} from '../../../../actions/snackbars/SnackBarsActions';
import { useAuthUserId } from '../../../../selectors/AuthSelectors';
import { useRestaurantId } from '../../../../selectors/RestaurantSelectors';
import { getPrepaySettingsRequest, updatePrepaySettingsRequest } from '../api/api';
import { mapDomainToDTO, mapDTOToDomain } from './mappers';
import {
  PrepayAmountMode,
  PrepaymentAcceptanceScheduleMode,
  PrepaySettings,
  PrepaySettingsDTO,
} from './types';

export const PAYMENT_BANKS: readonly { value: string; label: string }[] = [
  { value: 'ALFA_BANK', label: 'Альфа-Банк' },
  { value: 'SBERBANK', label: 'Сбербанк' },
  { value: 'T_BANK', label: 'Т-Банк' },
  { value: 'RAIFFEISEN', label: 'Райффайзен Банк' },
  { value: 'PSKB', label: 'ПСКБ' },
  { value: 'RUSSIAN_STANDARD_BANK', label: 'Русский Стандарт' },
  { value: 'KASPI', label: 'Kaspi' },
];

export const PREPAY_AMOUNT_MODES: readonly { value: PrepayAmountMode; label: string }[] = [
  { value: 'MANUAL', label: 'Ввод вручную. Всегда указываем сумму при создании резерва' },
  { value: 'AUTO_PERSONS', label: 'Автоматический расчет суммы предоплаты по количеству гостей' },
];

export const PREPAYMENT_ACCEPTANCE_SCHEDULE_VARIANTS: readonly {
  value: PrepaymentAcceptanceScheduleMode;
  label: string;
}[] = [
  { value: 'RESTAURANT_SCHEDULE', label: 'По графику работы ресторана' },
  { value: 'CUSTOM_SETTINGS', label: 'Индивидуальные настройки' },
];

export function usePrepaySettingsQueryKey() {
  return [useAuthUserId(), 'prepaySettings', useRestaurantId()];
}

export function usePrepaySettings() {
  const restaurantId = useRestaurantId();

  const { data, isLoading, isError } = useQuery({
    staleTime: 60 * 1000 * 5,
    refetchOnWindowFocus: false,
    enabled: !!restaurantId,
    queryKey: usePrepaySettingsQueryKey(),
    queryFn: () => (restaurantId ? getPrepaySettingsRequest(restaurantId) : undefined),
    select: (response) => (response?.data ? mapDTOToDomain(response.data) : undefined),
  });

  return {
    isLoading,
    isError,
    data,
  };
}

export function useUpdatePrepaySettingsMutation() {
  const queryClient = useQueryClient();
  const prepaySettingsQueryKey = usePrepaySettingsQueryKey();
  const restaurantId = useRestaurantId();
  const dispatch = useDispatch();

  return useMutation<{ data: PrepaySettingsDTO }, AxiosError, PrepaySettings>({
    mutationFn: async (settings: PrepaySettings) => {
      if (!restaurantId) {
        throw new Error('Restaurant ID is required');
      }
      const dto = mapDomainToDTO(settings);
      return updatePrepaySettingsRequest(restaurantId, dto);
    },

    onSuccess: (response) => {
      dispatch(customMessageShackBar('Настройки предоплаты сохранены'));
      queryClient.setQueryData(prepaySettingsQueryKey, response);
    },

    onError: (error) => {
      console.error('Failed to update prepay settings:', error);
      dispatch(customErrorShackBar('Ошибка сохранения настроек предоплаты'));
    },
  });
}

export const generateEventDelayMinutesOptions = (min: number, max: number, interval: number) => {
  return Array.from({ length: Math.ceil((max - min) / interval) + 1 }, (_, i) => ({
    value: min + i * interval,
    label: `${min + i * interval} минут`,
  }));
};
